/**
 * Time Machine — full-record replay + selective agent-only revert.
 *
 * WHY: the auditor flagged that Time Machine existed only as a backend
 * ledger cut (src/lib/crm/timeMachine.ts) with no UI and no replay. This
 * module is the PURE engine behind the record scrubber: given a record's
 * field-change history it can
 *
 *   • reconstruct the record's state at ANY past instant ({@link replayTo}),
 *   • list the scrubber stops ({@link snapshotsAt}),
 *   • diff two states into reversal-compatible cell edits ({@link diffStates}),
 *   • compile a governed restore plan ({@link revertPlan}), and
 *   • strip ONLY agent-authored changes while preserving every human edit
 *     ({@link agentOnlyRevert}) — the selective revert Salesforce field
 *     history structurally cannot express.
 *
 * Nothing here writes. A plan's changes are submitted through the EXISTING
 * reversal → approvals pipeline (src/lib/agent/reversal.ts semantics:
 * a revert is just another governed proposal), never a direct mutation.
 */

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** A record's field values at one instant. */
export type RecordState = Record<string, unknown>;

/** One sealed field change in a record's history. */
export type FieldChange = {
  attr: string;
  from: unknown;
  to: unknown;
  /** Epoch millis when the change landed. */
  at: number;
  actor: { kind: 'human' | 'agent'; id: string };
  /** Decision Ledger chain hash covering this change, when sealed. */
  ledgerSeal?: string;
};

/**
 * One diffStates row — shape-compatible with `CellEdit` from
 * src/lib/crm/inlineEditDiff.ts minus the recordId (callers stamp it),
 * i.e. the same old→new story the reversal engine restores from.
 */
export type CellEditLike = { attr: string; from: unknown; to: unknown };

/** Chronological (asc, stable) copy of a change list. */
function chronological(changes: FieldChange[]): FieldChange[] {
  return changes
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (a.c.at - b.c.at) || (a.i - b.i))
    .map((x) => x.c);
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

/**
 * Reconstruct the record state at `atOrBefore`: start from `base` (the
 * state BEFORE any of the changes) and apply every change with
 * `at <= atOrBefore` in chronological order. Pure — returns a new object;
 * neither `base` nor `changes` is mutated.
 */
export function replayTo(
  base: RecordState,
  changes: FieldChange[],
  atOrBefore: number,
): RecordState {
  const state: RecordState = { ...base };
  for (const c of chronological(changes)) {
    if (c.at > atOrBefore) break;
    state[c.attr] = c.to;
  }
  return state;
}

/**
 * The scrubber stops: every distinct change timestamp, ascending. Each stop
 * is an instant `replayTo` can reconstruct. Pure; empty history → [].
 */
export function snapshotsAt(changes: FieldChange[]): number[] {
  return [...new Set(changes.map((c) => c.at))].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Diff / revert planning
// ---------------------------------------------------------------------------

/**
 * The cell edits that turn state `a` into state `b`: one row per attr whose
 * value differs (union of both key sets, sorted for determinism). Pure.
 */
export function diffStates(a: RecordState, b: RecordState): CellEditLike[] {
  const attrs = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
  const out: CellEditLike[] = [];
  for (const attr of attrs) {
    const from = a[attr];
    const to = b[attr];
    if (!Object.is(from, to)) out.push({ attr, from, to });
  }
  return out;
}

/**
 * Compile the governed plan that restores `current` to `target`: one
 * reversal-compatible FieldChange per differing attr, carrying the true
 * old→new story (`from` = current value, `to` = target value). The plan is
 * a PROPOSAL — `at` is 0 and the actor is the human time-machine operator —
 * because the real timestamp/seal is stamped by the approvals pipeline the
 * plan must be submitted through (reversal.ts contract: undo is just
 * another governed proposal, never a direct write). Pure.
 */
export function revertPlan(
  current: RecordState,
  target: RecordState,
): { changes: FieldChange[] } {
  return {
    changes: diffStates(current, target).map((d) => ({
      attr: d.attr,
      from: d.from,
      to: d.to,
      at: 0,
      actor: { kind: 'human', id: 'time-machine' },
    })),
  };
}

/**
 * Selective agent-only revert: strip every change authored by an agent
 * while PRESERVING every human edit — per attr, the resulting value is the
 * `to` of the latest human change, or (when only agents ever touched the
 * attr) the `from` captured before the first change, i.e. fully undone.
 * Changes dated after `now` are ignored (they haven't happened yet).
 *
 * Returns the split (`keep` = human changes, `undo` = agent changes) plus
 * the `resulting` state — attrs untouched by any change keep their current
 * value. Pure: `current` and `changes` are never mutated, and applying the
 * result is the caller's job via the governed reversal pipeline.
 */
export function agentOnlyRevert(
  current: RecordState,
  changes: FieldChange[],
  now: number,
): { keep: FieldChange[]; undo: FieldChange[]; resulting: RecordState } {
  const applied = chronological(changes).filter((c) => c.at <= now);
  const keep = applied.filter((c) => c.actor.kind !== 'agent');
  const undo = applied.filter((c) => c.actor.kind === 'agent');

  const resulting: RecordState = { ...current };
  const attrs = new Set(applied.map((c) => c.attr));
  for (const attr of attrs) {
    const chain = applied.filter((c) => c.attr === attr);
    const humans = chain.filter((c) => c.actor.kind !== 'agent');
    resulting[attr] =
      humans.length > 0 ? humans[humans.length - 1].to : chain[0].from;
  }
  return { keep, undo, resulting };
}

/**
 * How much of the history is ledger-sealed: `sealed` counts changes carrying
 * a non-empty `ledgerSeal`. Honest zeros for an empty history. Pure.
 */
export function sealCoverage(changes: FieldChange[]): { total: number; sealed: number } {
  let sealed = 0;
  for (const c of changes) {
    if (typeof c.ledgerSeal === 'string' && c.ledgerSeal !== '') sealed++;
  }
  return { total: changes.length, sealed };
}
